/**
 * MeetPadlaWall — экран знакомства («сначала чат с Падлой», решение ректора 08.08.2026).
 *
 * Что человек видит. Он нажал ссылку — чаще всего присланную другом («проверим
 * совместимость») — и оказался внутри нашего окна, не будучи знаком с Падлой. Раньше
 * окно просто работало, а Падла не мог прислать ему ни строчки: переписки нет —
 * «отправили» не равно «дошло» (класс закрыт 8 авг: 13 из 40 Искр умерли в chat not found).
 * Теперь вместо тихой пустоты — одна понятная дверь в переписку.
 *
 * Почему экран один на все входы (кабинет, астрология, песни, тесты): человек не знает
 * и не должен знать, в какую из наших дверей он вошёл — знакомство у него одно.
 * Поэтому у стены собственные стили, не наследующие тему конкретной студии.
 *
 * Тексты — Gemini (канон: русский user-facing копирайт пишет она, 8 авг), внесены буквально.
 */

import { useEffect } from 'react';
import './meet-padla-wall.css';

const apiBase = (import.meta.env.VITE_API_BASE || '/api').replace(/\/$/, '');

interface TelegramWebAppLite {
  openTelegramLink?: (url: string) => void;
  close?: () => void;
}

function telegram(): TelegramWebAppLite | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebAppLite } }).Telegram?.WebApp;
}

/** Шаг воронки стены → публичный RUM-приёмник (летит до авторизации, П25: замер на каждом шаге). */
function reportStep(step: 'shown' | 'open_chat'): void {
  try {
    const body = JSON.stringify({ type: 'meet_wall', step });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${apiBase}/v1/miniapp/rum`, body);
      return;
    }
    void fetch(`${apiBase}/v1/miniapp/rum`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* замер никогда не мешает человеку идти дальше */
  }
}

export interface MeetPadlaWallProps {
  /** Дверь в переписку — строит сервер (`bot_url`), уже с намерением человека в start-параметре. */
  botUrl: string;
  /** 'pair' — человека позвал друг на совместимость: ему важно, что ответ придёт ему. */
  variant?: 'default' | 'pair';
}

export function MeetPadlaWall({ botUrl, variant = 'default' }: MeetPadlaWallProps): JSX.Element {
  useEffect(() => reportStep('shown'), []);

  const openChat = (): void => {
    reportStep('open_chat');
    const tg = telegram();
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(botUrl);
      // openTelegramLink окно НЕ закрывает (Bot API 7.0+) — закрываем сами, иначе человек
      // возвращается на ту же стену поверх уже открытой переписки.
      setTimeout(() => tg.close?.(), 120);
      return;
    }
    window.open(botUrl, '_blank', 'noopener');
  };

  return (
    <div className="meet-wall">
      <div className="meet-wall__card">
        <div className="meet-wall__mark">🌒</div>
        <h1 className="meet-wall__title">Давайте для начала познакомимся</h1>
        <p className="meet-wall__body">
          {variant === 'pair'
            ? 'Чтобы разобрать вашу совместимость, мне нужно куда-то прислать ответ. Я пишу в обычные сообщения, поэтому первым делом нужно открыть нашу переписку.'
            : 'Я живу в обычных сообщениях, все ответы и расчёты присылаю туда же. Поэтому первый шаг — это открыть нашу переписку.'}
        </p>
        <button type="button" className="meet-wall__cta" onClick={openChat}>
          Открыть переписку
        </button>
        <p className="meet-wall__hint">Там нужно будет нажать «Начать», и мы продолжим.</p>
      </div>
    </div>
  );
}

export default MeetPadlaWall;
